import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Grid,
  Paper,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import {
  CheckCircle as ActiveIcon,
  HourglassEmpty as PendingIcon,
  Block as DisabledIcon,
  ArrowBack as BackIcon,
  ToggleOff as DisableIcon,
  ToggleOn as EnableIcon,
  AdminPanelSettings as AdminIcon,
  PersonOff as RemoveAdminIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguageUrl } from '../../utils/languageUtils';
import { adminService } from '../../services/adminService';
import { 
  UserDetailDto, 
  MonitoredIdentityDto 
} from '../../types/admin';

const UserDetailPage: React.FC = () => {
  const { t, i18n } = useTranslation('admin');
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { token, user: currentUser } = useAuth();
  const getLanguageUrl = useLanguageUrl();

  const [user, setUser] = useState<UserDetailDto | null>(null);
  const [monitoredIdentities, setMonitoredIdentities] = useState<MonitoredIdentityDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [identitiesLoading, setIdentitiesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalIdentitiesCount, setTotalIdentitiesCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20,
  });
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isUpdatingAdmin, setIsUpdatingAdmin] = useState(false);

  const loadUserDetail = useCallback(async () => {
    if (!token || !userId) return;

    try {
      setLoading(true);
      setError(null);
      const userData = await adminService.getUserById(userId, token);
      setUser(userData);
    } catch (err: any) {
      console.error('Failed to load user details:', err);
      setError(err.message || 'Failed to load user details. Please try again.');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [userId, token]);

  const loadMonitoredIdentities = useCallback(async () => {
    if (!token || !userId) return;

    try {
      setIdentitiesLoading(true);
      const result = await adminService.getUserMonitoredIdentities(
        userId,
        paginationModel.page + 1, // DataGrid uses 0-based, API uses 1-based
        paginationModel.pageSize,
        token
      );
      setMonitoredIdentities(result.items);
      setTotalIdentitiesCount(result.totalCount);
    } catch (err: any) {
      console.error('Failed to load monitored identities:', err);
      setMonitoredIdentities([]);
      setTotalIdentitiesCount(0);
    } finally {
      setIdentitiesLoading(false);
    }
  }, [userId, paginationModel, token]);

  useEffect(() => {
    loadUserDetail();
  }, [loadUserDetail]);

  useEffect(() => {
    loadMonitoredIdentities();
  }, [loadMonitoredIdentities]);

  const getStatusChip = (status: string) => {
    const config = {
      pending: {
        icon: <PendingIcon />,
        color: 'warning' as const,
        label: t('userStatus.pending'),
      },
      active: {
        icon: <ActiveIcon />,
        color: 'success' as const,
        label: t('userStatus.active'),
      },
      disabled: {
        icon: <DisabledIcon />,
        color: 'error' as const,
        label: t('userStatus.disabled'),
      },
    };
    const statusConfig = config[status as keyof typeof config];
    return (
      <Chip
        icon={statusConfig.icon}
        label={statusConfig.label}
        color={statusConfig.color}
        size="small"
      />
    );
  };

  const getIdTypeLabel = (idType: string) => {
    const labels: { [key: string]: string } = {
      PersonalId: t('idType.personalId'),
      CompanyId: t('idType.companyId'),
      DIMEX: t('idType.dimex'),
      NITE: t('idType.nite'),
    };
    return labels[idType] || idType;
  };

  const handleToggleStatus = async () => {
    if (!user || !token || !userId) return;

    // Check if trying to disable self
    if (user.id === currentUser?.id && user.status === 'active') {
      setError(t('userDetail.cannotDisableSelf'));
      return;
    }

    try {
      setIsUpdatingStatus(true);
      const newStatus = !user.notificationsEnabled;

      // Optimistic update
      setUser({
        ...user,
        notificationsEnabled: newStatus,
        status: newStatus ? 'active' : 'disabled',
      });

      // API call
      const updated = await adminService.updateUserStatus(
        userId,
        { notificationsEnabled: newStatus },
        token
      );

      // Confirm with server response
      setUser(updated);
      setError(null);
    } catch (err: any) {
      // Revert on error
      setUser((prev) =>
        prev
          ? {
              ...prev,
              notificationsEnabled: !prev.notificationsEnabled,
              status: prev.notificationsEnabled ? 'active' : 'disabled',
            }
          : prev
      );
      setError(err.message || 'Failed to update user status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleToggleAdmin = async () => {
    if (!user || !token || !userId) return;

    try {
      setIsUpdatingAdmin(true);
      const newAdminStatus = !user.isAdmin;

      // Optimistic update
      setUser({ ...user, isAdmin: newAdminStatus });

      // API call
      const updated = await adminService.updateAdminPrivileges(
        userId,
        { isAdmin: newAdminStatus },
        token
      );

      setUser(updated);
      setError(null);
    } catch (err: any) {
      // Revert on error
      setUser((prev) => (prev ? { ...prev, isAdmin: !prev.isAdmin } : prev));
      setError(err.message || 'Failed to update admin privileges');
    } finally {
      setIsUpdatingAdmin(false);
    }
  };

  const handleBack = () => {
    navigate(getLanguageUrl('adminUserManagement'));
  };

  const columns: GridColDef[] = [
    {
      field: 'normalizedId',
      headerName: t('userDetail.identityColumns.id'),
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => row.nationalId?.normalizedId || '-',
    },
    {
      field: 'idType',
      headerName: t('userDetail.identityColumns.type'),
      width: 150,
      valueGetter: (value, row) => row.nationalId?.idType,
      valueFormatter: (value: number) => getIdTypeLabel(value?.toString() || '0'),
    },
    {
      field: 'fullName',
      headerName: t('userDetail.identityColumns.name'),
      flex: 1.5,
      minWidth: 200,
      valueGetter: (value, row) => row.nationalId?.fullName || row.fullName,
      valueFormatter: (value: string | null | undefined) => value || t('notResolved'),
    },
    {
      field: 'label',
      headerName: t('userDetail.identityColumns.label'),
      flex: 1,
      minWidth: 150,
      valueFormatter: (value: string | null | undefined) => value || t('notSet'),
    },
    {
      field: 'isActive',
      headerName: t('userDetail.identityColumns.status'),
      width: 100,
      renderCell: (params: GridRenderCellParams) =>
        params.value ? (
          <Chip label={t('active')} color="success" size="small" />
        ) : (
          <Chip label={t('inactive')} color="default" size="small" />
        ),
    },
    {
      field: 'existingMentionCount',
      headerName: t('userDetail.identityColumns.mentions'),
      width: 100,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value: number | null | undefined) => value ?? 0,
    },
    {
      field: 'createdAt',
      headerName: t('userDetail.identityColumns.createdAt'),
      width: 150,
      valueFormatter: (value: string | null | undefined) =>
        value ? new Date(value).toLocaleDateString(i18n.language) : '-',
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
          {t('userDetail.backToUserManagement')}
        </Button>
        <Alert severity="error">{t('userDetail.userNotFound')}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Button startIcon={<BackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
        {t('userDetail.backToUserManagement')}
      </Button>

      <Typography variant="h4" gutterBottom>
        {t('userDetail.title')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('userDetail.profile')}
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="body2" color="text.secondary">
              {t('userDetail.fields.email')}
            </Typography>
            <Typography variant="body1">{user.email}</Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="body2" color="text.secondary">
              {t('userDetail.fields.fullName')}
            </Typography>
            <Typography variant="body1">{user.fullName || t('notSet')}</Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="body2" color="text.secondary">
              {t('userDetail.fields.status')}
            </Typography>
            <Box sx={{ mt: 0.5 }}>{getStatusChip(user.status)}</Box>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="body2" color="text.secondary">
              {t('userDetail.fields.isAdmin')}
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              {user.isAdmin ? (
                <Chip icon={<AdminIcon />} label={t('yes')} color="primary" size="small" />
              ) : (
                <Chip label={t('no')} color="default" size="small" />
              )}
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="body2" color="text.secondary">
              {t('userDetail.fields.createdAt')}
            </Typography>
            <Typography variant="body1">
              {new Date(user.createdAt).toLocaleString(i18n.language)}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="body2" color="text.secondary">
              {t('userDetail.fields.emailConfirmedAt')}
            </Typography>
            <Typography variant="body1">
              {user.emailConfirmedAt
                ? new Date(user.emailConfirmedAt).toLocaleString(i18n.language)
                : t('notConfirmed')}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="body2" color="text.secondary">
              {t('userDetail.fields.country')}
            </Typography>
            <Typography variant="body1">{user.countryCode}</Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="body2" color="text.secondary">
              {t('userDetail.fields.language')}
            </Typography>
            <Typography variant="body1">{user.preferredLanguage}</Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="body2" color="text.secondary">
              {t('userDetail.fields.monitoredIdentities')}
            </Typography>
            <Typography variant="body1">{user.monitoredIdentityCount}</Typography>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color={user.status === 'active' ? 'error' : 'success'}
            onClick={handleToggleStatus}
            disabled={isUpdatingStatus || user.status === 'pending'}
            startIcon={
              isUpdatingStatus ? (
                <CircularProgress size={16} />
              ) : user.status === 'active' ? (
                <DisableIcon />
              ) : (
                <EnableIcon />
              )
            }
          >
            {user.status === 'active'
              ? t('userDetail.disableUser')
              : t('userDetail.enableUser')}
          </Button>

          <Button
            variant="outlined"
            color={user.isAdmin ? 'error' : 'primary'}
            onClick={handleToggleAdmin}
            disabled={isUpdatingAdmin}
            startIcon={
              isUpdatingAdmin ? (
                <CircularProgress size={16} />
              ) : user.isAdmin ? (
                <RemoveAdminIcon />
              ) : (
                <AdminIcon />
              )
            }
          >
            {user.isAdmin
              ? t('userDetail.revokeAdmin')
              : t('userDetail.grantAdmin')}
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('userDetail.monitoredIdentities')}
        </Typography>

        <Box sx={{ height: 400, width: '100%', mt: 2 }}>
          <DataGrid
            rows={monitoredIdentities}
            columns={columns}
            loading={identitiesLoading}
            rowCount={totalIdentitiesCount}
            pageSizeOptions={[10, 20, 50]}
            paginationModel={paginationModel}
            paginationMode="server"
            onPaginationModelChange={setPaginationModel}
            slots={{
              noRowsOverlay: () => (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                  }}
                >
                  <Typography color="text.secondary">
                    {t('userDetail.noMonitoredIdentities')}
                  </Typography>
                </Box>
              ),
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default UserDetailPage;
